import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { HarnessCapabilityEffort } from "@cohesive/api-client";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { MENU_CLASS, ROW_CLASS } from "./menuStyles";

/**
 * The thinking-effort pill: trigger carries the current effort, the menu each one's description.
 * With `onSelectDefault` the menu leads with a Default row, selected while `effort` is unset, so
 * a seat that stores no effort can show the default without pinning it.
 */
export function EffortPicker({
  efforts,
  effort,
  onSelect,
  defaultEffort,
  onSelectDefault,
  className,
  side = "top",
  disabled,
}: {
  efforts: HarnessCapabilityEffort[];
  effort: string | undefined;
  onSelect: (effort: string) => void;
  defaultEffort?: string;
  onSelectDefault?: () => void;
  className?: string;
  side?: "top" | "bottom";
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const labelOf = (id: string | undefined) =>
    efforts.find((e) => e.id === id)?.label ?? id;
  const defaultLabel = defaultEffort
    ? `Default (${labelOf(defaultEffort)})`
    : "Default";
  const label =
    onSelectDefault && effort === undefined ? defaultLabel : labelOf(effort);
  const select = (next: string | undefined, apply: () => void) => {
    setOpen(false);
    if (next !== effort) apply();
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={className}
              disabled={disabled}
              aria-label={`Thinking effort: ${label}`}
            >
              <span className="truncate">{label}</span>
              <ChevronDown className="h-3 w-3 shrink-0" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">Set thinking effort</TooltipContent>
      </Tooltip>

      <PopoverContent
        side={side}
        align="start"
        sideOffset={8}
        className={MENU_CLASS}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {onSelectDefault && (
          <EffortRow
            option={{
              id: "",
              label: defaultLabel,
              description: "Your default for this model.",
            }}
            selected={effort === undefined}
            // The caller decides: a stored effort the model no longer offers shows as Default
            // here but still needs clearing.
            onSelect={() => {
              setOpen(false);
              onSelectDefault();
            }}
          />
        )}
        {efforts.map((option) => (
          <EffortRow
            key={option.id}
            option={option}
            selected={option.id === effort}
            onSelect={() => select(option.id, () => onSelect(option.id))}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
}

function EffortRow({
  option,
  selected,
  onSelect,
}: {
  option: HarnessCapabilityEffort;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(ROW_CLASS, selected && "bg-accent/60")}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm">{option.label}</div>
        <div className="text-xs text-muted-foreground leading-snug">
          {option.description}
        </div>
      </div>
      {selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
    </button>
  );
}
